import type { App } from 'app/App';
import { GameEndFlowController } from 'game/GameEndFlowController';
import { GameAreaController } from 'game/boardMVC/controllers/GameAreaController';
import { LevelTasksTracker } from 'game/tasks/LevelTasksTracker';
import { GameSceneView } from 'game/views/GameSceneView';
import type { Container } from 'pixi.js';
import { EventScheduler } from 'utils/EventScheduler';
import type { TileModel } from 'game/boardMVC/models/board/TileModel';
import type { TrayModel } from 'game/boardMVC/models/tray/TrayModel';
import { AdErrorPopup } from 'game/views/popups/AdErrorPopup';
import { Signal } from 'typed-signals';
import cloneDeep from 'lodash/cloneDeep';
import { SoundTrackId, soundManager } from '../app/SoundManager';
import { EnemyBoardComponent } from './EnemyBoardComponent';
import { connectTaskTrackerToBoard } from '../game/tasks/connectTaskTrackerToBoard';
import type { BlockerModel } from '../game/boardMVC/types';
import { BlockerType } from '../game/boardMVC/types';
import type { BoardModel } from '../game/boardMVC/models/board/BoardModel';
import { BoardEventType } from '../game/boardMVC/models/board/BoardEvents';
import type { Scene } from '../app/SceneManager';
import { playLayoutAnimation } from '../libs/layouts/layoutAnimations/playLayoutAnimation';
import type { LevelData } from '../game/level/LevelData';
import { exitToMainMenu } from '../behaviours/exitToMainMenu';
import { BoosterController } from '../game/boardMVC/controllers/BoosterController';
import { BoosterPanelView } from '../game/BoosterPanelView';
import { GAME_ECONOMY_SETTINGS } from '../game/GameConstants';
import type { BoosterType } from '../game/boosters/types';
import type { ProgressSaveData } from '../app/state/AppStateController';
import { VersusPreviewOverlayLayout } from '../../layouts/gameJam/VersusPreviewOverlayLayout';

interface GameSceneControllerDependencies {
  app: App;
  root: Container;
}

export class VersusSceneController {
  private readonly levelTaskTracker: LevelTasksTracker;

  private readonly endFlowController: GameEndFlowController;

  private readonly sceneView: GameSceneView;

  private readonly gameAreaController: GameAreaController;

  private readonly scheduler = new EventScheduler();

  private paused = false;

  private readonly enemyComponent: EnemyBoardComponent;

  public readonly tutorial = null;

  public readonly onRevive = new Signal();

  public readonly onUndoStateCreated = new Signal<(progress: ProgressSaveData) => void>();

  public readonly onBoosterUsed = new Signal<(type: BoosterType) => void>();

  private readonly boosterController: BoosterController;

  private readonly boosterPanelView: BoosterPanelView;

  private startTimeStamp = 0;

  constructor(
    private readonly dependencies: GameSceneControllerDependencies,
    private readonly sceneOptions: { levelData: LevelData },
  ) {
    this.levelTaskTracker = this.initTaskTracker();
    const {
      dependencies: {
        app: { stateController, iap },
      },
    } = this;

    this.enemyComponent = new EnemyBoardComponent(
      sceneOptions.levelData,
      stateController.getData().versus?.difficulty ?? 0,
      !stateController.getData().versus?.afterFirstWin,
    );

    this.sceneView = new GameSceneView({
      state: stateController,
      taskTracker: this.levelTaskTracker,
      iapAvailable: !!iap,
      versusMode: {
        enemyComponent: this.enemyComponent,
        versusTopBarParams: {
          onPlayerProgressUpdate: this.levelTaskTracker.onProgressUpdated,
          onEnemyProgressUpdate: this.enemyComponent.taskTracker.onProgressUpdated,
        },
      },
    }).props({
      parent: this.dependencies.root,
    });

    this.gameAreaController = this.initGameArea();
    this.endFlowController = this.initEndFlowController();

    const { view, controller } = this.initBoosters();
    this.boosterController = controller;
    this.boosterPanelView = view;

    connectTaskTrackerToBoard({
      animateBoardProgress: (tilesOrBlocker: BlockerModel | TileModel[]): void => {
        void this.sceneView.animateBoardProgress(tilesOrBlocker);
      },
      boardModel: this.getBoardModel(),
      boardView: this.sceneView.getBoardView() ?? null,
      levelTaskTracker: this.levelTaskTracker,
      trayModel: this.getTrayModel(),
    });
    this.hookUpBoard();

    (window as unknown as { vsc: VersusSceneController }).vsc = this;
  }

  public getBoardModel(): BoardModel {
    return this.gameAreaController.boardModel;
  }

  public getTrayModel(): TrayModel {
    return this.gameAreaController.trayModel;
  }

  public getTasksTracker(): LevelTasksTracker {
    return this.levelTaskTracker;
  }

  public getSceneView(): GameSceneView {
    return this.sceneView;
  }

  public activate(): void {
    const {
      dependencies: {
        app: {
          controller: { sceneManager },
        },
      },
      levelTaskTracker,
      gameAreaController: { boardModel, trayModel },
    } = this;

    boardModel.onScoreUpdated(levelTaskTracker.getHexTally());
    trayModel.updateProgress(levelTaskTracker.getCombinedProgress());

    boardModel.beforeMakeMoveClean.connect(() => {
      this.onUndoStateCreated.emit(this.getSaveLevelData());
    });

    this.hookUpSceneViewEvents();

    void (async (): Promise<void> => {
      if (this.endFlowController.checkLevelWon() || this.endFlowController.checkOutOfSpace()) return;

      // TODO start animation
      const countdown = new VersusPreviewOverlayLayout();
      countdown.root.props({
        parent: countdown.root.pixi.stage,
      });
      await playLayoutAnimation(countdown, 'Appear');
      countdown.root.destroy({ children: true });

      trayModel.refillItems(false);
      void this.enemyComponent.start();

      this.startTimeStamp = Date.now();
    })();

    const onSceneActivated = (scene: Scene): void => {
      sceneManager.onSceneActivated.disconnect(onSceneActivated);
      if (scene.id !== 'PVP') this.stopMusic();
    };
    sceneManager.onSceneActivated.connect(onSceneActivated);
    this.startMusic();
  }

  private hookUpSceneViewEvents(): void {
    const { sceneView, gameAreaController } = this;

    sceneView.onScoreProgressDisplayed.connect((progress) => {
      gameAreaController.boardModel.onScoreUpdated(progress);
    });

    sceneView.onLastScoreProgressDisplayed.connect(() => {
      this.endFlowController.checkLevelWon();
    });
  }

  private initTaskTracker(): LevelTasksTracker {
    const {
      sceneOptions: { levelData },
    } = this;

    return new LevelTasksTracker(levelData, {});
  }

  private initGameArea(): GameAreaController {
    const gameAreaController = new GameAreaController({
      app: this.dependencies.app,
      levelData: this.sceneOptions.levelData,
      dragContainer: this.sceneView,
      isInBoosterMode: () => false,
      rewardedAdTrigger: async (): Promise<boolean> => {
        const response = await this.dependencies.app.ads?.showRewardedAd('locked-ad-space');

        if (response === 'Success') {
          return true;
        }

        if (response === 'Error') {
          await AdErrorPopup.show(this.dependencies.app.controller.popups);
        }

        return false;
      },
    });

    this.sceneView.addGameAreaViews(gameAreaController.view);

    return gameAreaController;
  }

  private initBoosters(): { controller: BoosterController; view: BoosterPanelView } {
    const {
      dependencies: {
        app: { stateController: state, resources },
      },
      gameAreaController: { boardModel, trayModel },
    } = this;

    const economyMap = GAME_ECONOMY_SETTINGS.boosters;

    const boosterController = new BoosterController({
      controller: this.gameAreaController,
      gameSceneController: this,
      resources,
      state,
      // purchase flow is locked
      iap: null,
    });

    boosterController.onBoosterUsed.connect((type) => {
      this.onBoosterUsed.emit(type);
    });

    const boosterPanelView = new BoosterPanelView(
      state,
      economyMap,
      (boosterType: BoosterType) => boosterController.canAffordBoosterAction(boosterType),
      (boosterType: BoosterType) => boosterController.canExecuteBoosterAction(boosterType),
    );

    boosterPanelView.hookupBoard(boardModel, trayModel);

    boosterPanelView.onBoosterClicked.connect(async (boosterType: BoosterType) => {
      if (state.getBoosterInventoryAmount(boosterType) === 0) {
        if (!resources.canAfford(GAME_ECONOMY_SETTINGS.boosters[boosterType].coinCost)) {
          return;
        }
        resources.buyBooster(boosterType);
      }

      void this.triggerBoosterUse(boosterType);
    });

    this.sceneView.addBoosterPanel(boosterPanelView);

    return { view: boosterPanelView, controller: boosterController };
  }

  public debug = {
    triggerWinFlow: (): void => {
      this.levelTaskTracker.debugCompleteAllTasks();
      this.endFlowController.onLevelWon.emit();
    },
    triggerFailFlow: (): void => {
      this.endFlowController.onOutOfSpace.emit();
    },
  };

  private initEndFlowController(): GameEndFlowController {
    const controller = new GameEndFlowController({
      boardModel: this.getBoardModel(),
      taskTracker: this.levelTaskTracker,
    });

    controller.onLevelWon.connect(async () => {
      void this.triggerGameOver(true);
    });

    controller.onOutOfSpace.connect(async () => {
      void this.triggerGameOver(false);
    });

    this.enemyComponent.endFlowController.onLevelWon.connect(() => {
      void this.triggerGameOver(false);
    });

    this.enemyComponent.endFlowController.onOutOfSpace.connect(() => {
      void this.triggerGameOver(true);
    });

    return controller;
  }

  private gameResolved = false;

  private async triggerGameOver(playerWon: boolean): Promise<void> {
    if (this.gameResolved) return;

    const {
      dependencies: { app },
    } = this;

    void this.cancelBoosterUse();

    this.enemyComponent.stop();

    this.gameResolved = true;

    await app.gameEvents.versus?.openVersusResultPopup(playerWon, Date.now() - this.startTimeStamp);

    exitToMainMenu(this.dependencies.app);
  }

  private hookUpBoard(): void {
    const {
      app: { tracking, stateController },
    } = this.dependencies;

    const boardModel = this.getBoardModel();

    boardModel.onStateUpdate.connect(() => {
      if (this.paused) return;
      this.endFlowController.checkOutOfSpace();
    });

    boardModel.events.addListener(BoardEventType.BlockerRemoved, ({ blocker }) => {
      if (blocker.getType() === BlockerType.Ad) {
        tracking.trackRvTileUnlock({
          levelAttemptId: stateController.getLevelTrackingData().levelAttemptId,
          trigger: 'ad',
        });
      }
    });
  }

  private startMusic(): void {
    soundManager.playLoop(SoundTrackId.Music, 'Gameplay_Music', 100);
  }

  private stopMusic(): void {
    soundManager.fadeOutSound(SoundTrackId.Music, 'Gameplay_Music', 100, true);
  }

  private async triggerBoosterUse(boosterType: BoosterType, hideCancel = false): Promise<void> {
    const { boosterController, sceneView, boosterPanelView } = this;

    const requiresOverlay = !boosterController.isInstantAction(boosterType);
    if (requiresOverlay)
      await sceneView.showBoosterUserOverlay(
        boosterType,
        hideCancel
          ? null
          : (): void => {
              void this.cancelBoosterUse();
            },
      );

    await boosterController.executeBoosterAction(boosterType);

    // TODO HS-307 check if that's really necessary
    boosterPanelView.updateButtonStates();
    if (requiresOverlay) await sceneView.hideBoosterUserOverlay();
  }

  private async cancelBoosterUse(): Promise<void> {
    if (!this.boosterController.hasActiveAction()) return;
    this.boosterController.cancelActiveAction();
    await this.sceneView.hideBoosterUserOverlay();
  }

  public pauseGame(): void {
    this.paused = true;
    this.scheduler.paused = true;
    this.gameAreaController.getBoardView().pauseTweens(true);
  }

  public resumeGame(): void {
    this.paused = false;
    this.scheduler.paused = false;
    this.gameAreaController.getBoardView().resumeTweens(true);
  }

  public update(deltaTime: number): void {
    this.scheduler.update(deltaTime);
    this.sceneView.update(deltaTime);
    if (this.paused) return;
    this.gameAreaController.update(deltaTime);
  }

  public destroy(): void {
    this.sceneView.destroy();
    this.gameAreaController.destroy();
  }

  public exportLevelData(): LevelData {
    const {
      gameAreaController: { boardModel, trayModel },
    } = this;

    return {
      ...this.sceneOptions.levelData,
      board: boardModel.exportState(),
      tray: trayModel.exportState(),
      tutorial: { steps: [] },
    };
  }

  private getSaveLevelData(): ProgressSaveData {
    const { board, tray } = this.exportLevelData();

    return {
      levelData: { board, tray, tutorial: { steps: [] } },
      levelTaskTallies: cloneDeep(this.levelTaskTracker.getTaskTallies()),
      levelReviveUsed: 0,
    };
  }
}
